export const PERMISSION_ACTION_FIELDS = [
  "isView",
  "isCreate",
  "isEdit",
  "isDelete",
];

const emptyPermissionGroup = {
  selecionarTodos: false,
  isView: false,
  isCreate: false,
  isEdit: false,
  isDelete: false,
};

/**
 * Elegibilidade do switch temporário "devMode" no editor de permissões.
 * Usa APENAS o usuário autenticado (operador), nunca o usuário/perfil editado.
 */
export function canUsePermissionDevMode(operator) {
  return operator?.devMode === true && operator?.viewALL === true;
}

/** Sem adaptação: matriz completa (allowedPermissions ausente/null). */
export function isUnscopedPermissionMode(allowedPermissions) {
  return allowedPermissions == null;
}

export function canGrantPermission(allowedPermissions, groupKey, fieldKey) {
  if (!groupKey) return false;
  if (isUnscopedPermissionMode(allowedPermissions)) {
    if (fieldKey === "selecionarTodos" || fieldKey === "single") return true;
    return PERMISSION_ACTION_FIELDS.includes(fieldKey);
  }
  if (fieldKey === "single") {
    return Boolean(allowedPermissions?.[groupKey]?.isView);
  }
  if (fieldKey === "selecionarTodos") {
    return groupHasGrantablePermission(allowedPermissions, groupKey);
  }
  return Boolean(allowedPermissions?.[groupKey]?.[fieldKey]);
}

export function groupHasGrantablePermission(allowedPermissions, groupKey) {
  if (!groupKey) return false;
  if (isUnscopedPermissionMode(allowedPermissions)) return true;
  return PERMISSION_ACTION_FIELDS.some((fieldKey) =>
    Boolean(allowedPermissions?.[groupKey]?.[fieldKey]),
  );
}

function getPermissionKeys(targetPermissoes, allowedPermissions, fallbackPermissoes) {
  return Array.from(
    new Set([
      ...Object.keys(fallbackPermissoes || {}),
      ...Object.keys(targetPermissoes || {}),
      ...Object.keys(allowedPermissions || {}),
    ]),
  );
}

export function recalculateGroupSelecionarTodos(group, allowedGroup) {
  const fieldsToCheck = allowedGroup
    ? PERMISSION_ACTION_FIELDS.filter((fieldKey) => allowedGroup?.[fieldKey])
    : PERMISSION_ACTION_FIELDS;

  if (!fieldsToCheck.length) return false;
  return fieldsToCheck.every((fieldKey) => Boolean(group?.[fieldKey]));
}

export function clampPermissoesToAllowed(
  targetPermissoes = {},
  allowedPermissions = {},
  fallbackPermissoes = {},
) {
  if (isUnscopedPermissionMode(allowedPermissions)) {
    return targetPermissoes;
  }

  const result = {};
  const keys = getPermissionKeys(
    targetPermissoes,
    allowedPermissions,
    fallbackPermissoes,
  );

  keys.forEach((groupKey) => {
    const targetGroup = targetPermissoes?.[groupKey] || {};
    const allowedGroup = allowedPermissions?.[groupKey] || {};
    const fallbackGroup = fallbackPermissoes?.[groupKey] || emptyPermissionGroup;

    result[groupKey] = {
      selecionarTodos: false,
      isView: allowedGroup.isView
        ? Boolean(targetGroup.isView)
        : Boolean(fallbackGroup.isView),
      isCreate: allowedGroup.isCreate
        ? Boolean(targetGroup.isCreate)
        : Boolean(fallbackGroup.isCreate),
      isEdit: allowedGroup.isEdit
        ? Boolean(targetGroup.isEdit)
        : Boolean(fallbackGroup.isEdit),
      isDelete: allowedGroup.isDelete
        ? Boolean(targetGroup.isDelete)
        : Boolean(fallbackGroup.isDelete),
    };

    result[groupKey].selecionarTodos = recalculateGroupSelecionarTodos(
      result[groupKey],
      allowedGroup,
    );
  });

  return result;
}

export function toggleScopedPermission(permissoes, allowedPermissions, groupKey, fieldKey) {
  if (!canGrantPermission(allowedPermissions, groupKey, fieldKey)) {
    return permissoes;
  }

  const currentGroup = permissoes?.[groupKey] || emptyPermissionGroup;
  const allowedGroup = isUnscopedPermissionMode(allowedPermissions)
    ? null
    : allowedPermissions?.[groupKey] || {};
  const next = { ...permissoes };

  if (fieldKey === "selecionarTodos") {
    const grantableFields = isUnscopedPermissionMode(allowedPermissions)
      ? [...PERMISSION_ACTION_FIELDS]
      : PERMISSION_ACTION_FIELDS.filter(
          (permissionField) => allowedGroup?.[permissionField],
        );
    const shouldEnable = !grantableFields.every((permissionField) =>
      Boolean(currentGroup?.[permissionField]),
    );

    next[groupKey] = {
      ...currentGroup,
      ...grantableFields.reduce(
        (acc, permissionField) => ({
          ...acc,
          [permissionField]: shouldEnable,
        }),
        {},
      ),
    };
  } else {
    next[groupKey] = {
      ...currentGroup,
      [fieldKey]: !currentGroup?.[fieldKey],
    };
  }

  next[groupKey].selecionarTodos = recalculateGroupSelecionarTodos(
    next[groupKey],
    allowedGroup,
  );

  if (groupKey === "freight" && fieldKey === "selecionarTodos") {

    if (next[groupKey].selecionarTodos === true) {
      const grupoLigado = {
        selecionarTodos: true, isView: true,
        isCreate: true, isEdit: true, isDelete: true,
      };
      next["freightPricing"] = { ...grupoLigado };
      next["freightDriver"] = { ...grupoLigado };
      next["freightFreight"] = { ...grupoLigado };
      next["freightCannotRelease"] = { ...grupoLigado };
      next["freightCancel"] = { ...grupoLigado };
      next["freightReopen"] = { ...grupoLigado };
      next["freightStoppingPointLatLong"] = { ...grupoLigado };
    }
    else if (next[groupKey].selecionarTodos === false) {
      const grupoDesligado = {
        selecionarTodos: false, isView: false,
        isCreate: false, isEdit: false, isDelete: false,
      };
      next["freightPricing"] = { ...grupoDesligado };
      next["freightDriver"] = { ...grupoDesligado };
      next["freightFreight"] = { ...grupoDesligado };
      next["freightCannotRelease"] = { ...grupoDesligado };
      next["freightCancel"] = { ...grupoDesligado };
      next["freightReopen"] = { ...grupoDesligado };
      next["freightStoppingPointLatLong"] = { ...grupoDesligado };
    }
  }


  return next;
}

export function calcularSelecionarTodosGeralPorEscopo(
  permissoes,
  allowedPermissions,
) {
  if (!permissoes || typeof permissoes !== "object") return false;

  if (isUnscopedPermissionMode(allowedPermissions)) {
    const groupKeys = Object.keys(permissoes);
    if (!groupKeys.length) return false;
    return groupKeys.every((groupKey) =>
      recalculateGroupSelecionarTodos(permissoes?.[groupKey], null),
    );
  }

  const grantableGroups = Object.keys(allowedPermissions || {}).filter((groupKey) =>
    groupHasGrantablePermission(allowedPermissions, groupKey),
  );

  if (!grantableGroups.length) return false;

  return grantableGroups.every((groupKey) =>
    recalculateGroupSelecionarTodos(
      permissoes?.[groupKey],
      allowedPermissions?.[groupKey],
    ),
  );
}

export function toggleSelecionarTodosGeralPorEscopo(
  permissoes,
  allowedPermissions,
) {
  const shouldEnable = !calcularSelecionarTodosGeralPorEscopo(
    permissoes,
    allowedPermissions,
  );
  const next = { ...permissoes };

  const groupKeys = isUnscopedPermissionMode(allowedPermissions)
    ? Object.keys(permissoes || {})
    : Object.keys(allowedPermissions || {});

  groupKeys.forEach((groupKey) => {
    if (!groupHasGrantablePermission(allowedPermissions, groupKey)) return;

    const currentGroup = permissoes?.[groupKey] || emptyPermissionGroup;
    const allowedGroup = isUnscopedPermissionMode(allowedPermissions)
      ? null
      : allowedPermissions?.[groupKey] || {};
    const grantableFields = isUnscopedPermissionMode(allowedPermissions)
      ? [...PERMISSION_ACTION_FIELDS]
      : PERMISSION_ACTION_FIELDS.filter(
          (fieldKey) => allowedGroup?.[fieldKey],
        );

    next[groupKey] = {
      ...currentGroup,
      ...grantableFields.reduce(
        (acc, fieldKey) => ({
          ...acc,
          [fieldKey]: shouldEnable,
        }),
        {},
      ),
    };
    next[groupKey].selecionarTodos = recalculateGroupSelecionarTodos(
      next[groupKey],
      allowedGroup,
    );
  });

  return next;
}
